import axios, { type AxiosRequestConfig } from "axios";
import { proxyUrl, serverHost } from "../config";
import Auth from "@/Auth/Auth";
import { UserType } from "./UserService";
import { ISuitabilityTest } from "./SuitabilityTestsService";
import { setupInterceptorsTo } from "./Interceptors";
import { UserIdentityStatusEnum } from "../components/types/UserIdentity.Status";
import { Identity } from "@/screen/Identities/Interfaces/IdentitiesMainScreenInterfaces";
setupInterceptorsTo(axios);

interface IUserIdentity
{
    id?: number;
    firstName?: string;
    lastName?: string;
    secondLastName?: string;
    idDocument?: string;
    idExpiration?: Date;
    birthday?: Date;
    birthdayCountry?: string;
    nationalCountry?: string;
    idType?: any;
    idFront?: any;
    idBack?: any;
    accountType?: string;
    proofOfMinorGuardianDescription?: any;
    email?: string;
    countryPrefix?: string;
    phone?: string;
    operationCountryPrefix?: string;
    operationPhone?: string;
    signCountryPrefix?: string;
    signPhone?: string;
    economicActivity?: number;
    proofOfIncome?: any;
    profession?: number;
    verificationCode?: string;
    verificationCodeSentAt?: Date;
    signCode?: string;
    signCodeSentAt?: Date;
    gafiQuestion?: boolean;
    publicPosition?: boolean;
    fiscalAddressId?: string;
    postalAddressId?: string;
    sameAddress?: boolean;
    main?: boolean;
    finametrixId?: string | null;
    updatedAt?: Date;
    savingsPlansUserIdentities?: any;
    economicStudies?: any;
    financialExperience?: any;
    investmentExperience?: any;
    fiscalAddress?: any;
    postalAddress?: any;
    status?: UserIdentityStatusEnum;
    suitabilityTests?: ISuitabilityTest[];
    userId?: number;
    maxRiskAllowed?: number;
    user?: UserType;
    createdAt?: Date;
    fnmContractsUserIdentities?: any[];
    canActivateSuitabilityTestButton?: boolean;
    fromAccessRequest?: boolean;
    showAuthorizationIcon?: boolean;
    showPendingAuthorizationButton?: boolean;
    authorizationStatus?: number;
    accessRequestId?: number;
    maxContribution?: number;
}
export type UserIdentityType = IUserIdentity;

export interface MainUserIdentityDto
{
    id: number;
    firstName: string;
    lastName: string;
    secondLastName: string;
    status: string;
}

export interface AmlVerificationResponse
{
    omnia: ServiceResult;
    pibisi: PibisiResult;
    userIdentityRisk: ServiceResult;
    uploadDocuments: ServiceResult;
    isProfileRisk: boolean;
    fullySuccess: boolean;
    summaryMessage: string;
}

export interface ServiceResult
{
    success: boolean;
    message: string;
    statusCode: number;
    details?: any | null;
}

export interface PibisiResult extends ServiceResult
{
    rawRiskResponse: string;
}

/**
 * Ítem de `data` en la respuesta orquestada de `GET .../userIdentities/jobs`
 * (`success`, `data`, `flow`, `errors`).
 */
export interface UserIdentityJobOrchestratorItemDto
{
    code: string;
    description: string;
    nextPortfolioId: string;
}

/** Entrada del desplegable de profesión en el formulario de identidad (proviene de `getJobs().data`). */
export interface UserIdentityProfessionCatalogItemDto
{
    code: string;
    description: string;
    nextPortfolioId?: string;
}

/** Ítem de los catálogos de profesión de Omnia (`GET .../userIdentities/professionCatalogs`). */
export interface ProfessionCatalogItemDto
{
    code: string;
    description: string;
    nextportfolioId?: string | null;
}

/**
 * Respuesta de `GET .../userIdentities/professionCatalogs/{catalog?}`
 * Lista no solicitada = null (≠ [] = catálogo vacío).
 */
export interface ProfessionCatalogsDto
{
    cnos?: ProfessionCatalogItemDto[] | null;
    cnaes?: ProfessionCatalogItemDto[] | null;
}

export type AccessRequestResponse =
{
    status: number;
    count: number;
    data: AccessRequestDto[];
};

export type AccessRequestMode =
{
    mode: string;
};

export interface AccessRequestDto
{
    id: number;
    requestingUserId: number;
    targetUserIdentityId: number;
    status: UserIdentityRequestStatusEnum;
    approvalDate?: string | null;
    updatedAt: string;
    sign: number;
    targetUserIdentity?: UserIdentityType | null;
    requestingUserIdentity?: UserIdentityType | null;
    requestedUserIdentity?: UserIdentityType | null;
}

export enum UserIdentityRequestStatusEnum
{
    Pending = 0,
    Approved = 1,
    Rejected = 2,
}
export interface UserIdentityAccessRequest
{
    id: number;
    requestingUserId: number;
    requestingUser?: UserType;
    requestedUserId: number;
    requestedUser?: UserType;
    targetUserIdentityId: number;
    targetUserIdentity?: UserIdentityType;
    status: UserIdentityRequestStatusEnum;
    approvalDate?: string | null;
    sign: string;
    createdAt: string;
    updatedAt: string;
}

/** Ítem de `errors[]` en el envelope del orquestador Identity. */
export type IdentityOrchestratorErrorItem =
{
    code: string;
    message: string;
};

/** Alias histórico; mismo contrato que `IdentityOrchestratorErrorItem`. */
export type OperationError = IdentityOrchestratorErrorItem;
/**
 * Descriptor `flow` del envelope. `existingIdentityId` lo informa el backend cuando la acción se refiere a
 * otro interviniente ya existente (p. ej. el 409 del guardado con un DNI que ya está en la plataforma).
 */
export type IdentityOrchestratorFlowDescriptor = { action: string; existingIdentityId?: number | null } | null;

/** Acciones de flijo fijas devueltas en `flow.action` */
export type UserIdentityFlowActionString =
    | "StartNewOnboarding"
    | "ResumeDraft"
    | "RequestAccess"
    | "InformationManualSignUpRequired"
    | "RequestOwnershipTransfer"
    | "AutomaticOwnershipTransfer"
    | "ReactivateOwn"
    | "AcceptTransferOwnership"
    | "RequiresManualReview";

/**
 * Respuesta unificada del orquestador Identity.
 * Transporte: revisar HTTP status; lógica fina: `success`, `data`, `flow`, `errors`.
 */
export type IdentityOrchestratorEnvelope<TData> =
{
    success: boolean;
    data: TData | Record<string, never> | null;
    flow: IdentityOrchestratorFlowDescriptor;
    errors: IdentityOrchestratorErrorItem[] | null;
};

export type EmptyIdentityOrchestratorEnvelope = IdentityOrchestratorEnvelope<Record<string, never>>;

const orchestratorValidateStatus = (status: number): boolean => status >= 200 && status < 600;

function redirectToLoginIfUnauthorized(status: number | undefined): boolean
{
    if(status === 401)
    {
        Auth.clearUserProfile();
        window.location.href = `${serverHost}/login`;
        return true;
    }
    return false;
}

export async function requestIdentityOrchestrator<TData>(config: AxiosRequestConfig): Promise<IdentityOrchestratorEnvelope<TData>>
{
    try
    {
        const req = await axios.request<IdentityOrchestratorEnvelope<TData>>(
        {
            ...config,
            validateStatus: orchestratorValidateStatus,
        });
        if(redirectToLoginIfUnauthorized(req.status))
        {
            return{
                success: false,
                data: null,
                flow: null,
                errors: [{code: "401", message: "Sesión no válida. Redirigiendo al inicio de sesión."}],
            };
        }
        const body = req.data;
        if(body && typeof body === "object" && "success" in body)
        {
            return body as IdentityOrchestratorEnvelope<TData>;
        }
        return{
            success: false,
            data: null,
            flow: null,
            errors: [{code: "INVALID_RESPONSE", message: "Respuesta inválida del servidor."}],
        };
    }
    catch(err: unknown)
    {
        return{
            success: false,
            data: null,
            flow: null,
            errors:
            [
                {
                    code: "NETWORK_ERROR",
                    message: axios.isAxiosError(err) ? (err.message || "Error de red.") : "Error de red.",
                },
            ],
        };
    }
}

export enum UserIdentityFlowAction
{
    StartNewOnboarding = 0,
    ResumeDraft = 1,
    RequestAccess = 2,
    InformationManualSignUpRequired = 3,
    RequestOwnershipTransfer = 4,
    AutomaticOwnershipTransfer = 5,
    /** El documento pertenece a un interviniente propio dado de baja: se puede recuperar. */
    ReactivateOwn = 6,
    /**
     * El documento pertenece a un interviniente propio en TRANSFER (recibido por cesión aprobada y aún sin
     * aceptar): no hay panel en el formulario actual, se abre su edición con el formulario de aceptación.
     */
    AcceptTransferOwnership = 7,
}

export interface DocumentAnalysisResultDto
{
    detectedDni?: string | null;
    // Solo el nombre de pila (campo FirstName del DNI); los apellidos llegan aparte en detectedLastName.
    detectedName?: string | null;
    detectedLastName?: string | null;
    detectedBirthDate?: string | null;
    detectedExpirationDate?: string | null;
    // Código ISO alfa-3 (p. ej. "ESP")
    detectedNationality?: string | null;
    detectedAddress?: DocumentAddressDto | null;
    extractedFields: Record<string, string>;
    confidence: number;
}

// Domicilio leído del documento (en el DNI, una línea abreviada y sin código postal)
export interface DocumentAddressDto
{
    rawText?: string | null;
    streetAddress?: string | null;
    road?: string | null;
    houseNumber?: string | null;
    unit?: string | null;
    level?: string | null;
    postalCode?: string | null;
    city?: string | null;
    state?: string | null;
    countryRegion?: string | null;
}

export enum IdentityDocumentAnalysisSideStatus
{
    Ok = 0,
    ValidationError = 1,
    AiError = 2,
    NotProcessed = 3,
}

export interface IdentityDocumentAnalysisResponse
{
    success: boolean;
    front?: DocumentAnalysisResultDto | null;
    back?: DocumentAnalysisResultDto | null;
    frontStatus: IdentityDocumentAnalysisSideStatus;
    backStatus: IdentityDocumentAnalysisSideStatus;
}

export interface UserIdentityStatusDto
{
    dni: string;
    action: UserIdentityFlowAction;
    existingDraftId?: number | null;
    existingPersonName?: string | null;
    requiresOmniaSync: boolean;
    isIncomplete: boolean;
}

/**
 * Fila de la matriz de estado KYC (`GET userIdentities/{id}/kyc/status`).
 * El estado lo decide el backend; el cliente solo lo pinta.
 */
export interface KycStatusRowDto
{
    userIdentityId: number;
    fullName: string;
    /** Estado del interviniente en IronIA (NEW, PENDING, ACTIVE, OUTDATED…). */
    status: string;
    /** True si tiene el doble enlace (Omnia + NextPortfolio) persistido. */
    synced: boolean;
    /** Estado KYC decidido por IronIA: SIGNED / PENDING_SIGNATURE / NOT_AVAILABLE / UNKNOWN. */
    kycStatus: string;
    /** Fecha de firma (solo cuando kycStatus es SIGNED). */
    signedAt?: string | null;
}

/**
 * Resumen de la comprobación de caducidad del KYC (`POST users/{userId}/kyc/expirationCheck`).
 * Solo recuentos: el detalle le llega al usuario por los avisos de la barra de estado que crea el propio
 * backend. Se dispara una única vez por login y su resultado no se pinta en ninguna pantalla.
 */
export interface KycExpirationSummaryDto
{
    /** Intervinientes propios recorridos (ACTIVE + OUTDATED). */
    evaluated: number;
    /** Intervinientes que deben firmar su KYC. */
    needSignature: number;
    /** Intervinientes que han pasado de ACTIVE a OUTDATED en esta comprobación. */
    flippedToOutdated: number;
    /** Intervinientes cuyo estado de firma no se ha podido verificar (no se tocan). */
    notVerifiable: number;
    /** Avisos de KYC vivos tras la reconstrucción. */
    activeNotices: number;
    /** True si la caducidad no está configurada en el backend y la comprobación no ha hecho nada. */
    expirationDisabled: boolean;
}

/**
 * Resultado de solicitar al propietario de un interviniente prestado que re-firme su KYC
 * (`POST userIdentities/{id}/kyc/notifyOwner`). Se distingue "creado ahora" de "ya lo tenía" para no
 * afirmarle al usuario que ha salido un aviso nuevo cuando la idempotencia lo ha evitado.
 */
export interface KycOwnerNotificationDto
{
    /** True si esta llamada ha creado el aviso. */
    notified: boolean;
    /** True si el propietario ya tenía una solicitud viva y no se ha creado otra. */
    alreadyNotified: boolean;
}

/** Valores posibles de <see cref="KycStatusRowDto.kycStatus"/> (constantes del backend). */
export const KycStatusValues =
{
    NotAvailable: "NOT_AVAILABLE",
    PendingSignature: "PENDING_SIGNATURE",
    Signed: "SIGNED",
    Unknown: "UNKNOWN",
} as const;

/**
 * Documento KYC servido para descarga/visualización (`GET userIdentities/{id}/kyc/document` ).
 * El PDF viaja en <see cref="content"/> como base64 dentro del envelope estándar.
 */
export interface KycDocumentDownloadDto
{
    mimeType: string;
    fileName: string;
    /** Contenido binario del PDF, en base64. */
    content: string;
    signed: boolean;
    signedAt?: string | null;
    generatedAt?: string | null;
}

/**
 * Resultado de la generación del documento KYC (`POST userIdentities/{id}/kyc`).
 * El estado del KYC vive en Omnia; IronIA no persiste nada de este DTO. La generación es idempotente:
 * si ya existe una versión pendiente devuelve la misma (mismo kycId), no crea otra.
 */
export interface KycDocumentDto
{
    kycId: string;
    documentUrl?: string | null;
    generatedAt?: string | null;
    /** Estado del KYC en Omnia (p. ej. PendingSignature / Signed). */
    status?: string | null;
}

/**
 * Firma OTP de la re-firma del KYC. DTO estrecho: nunca contiene el código de la firma.
 * `expiresAt` es null mientras la firma está creada pero DESARMADA (sin SMS enviado); se puebla al
 * armarla con `POST userIdentities/{id}/kyc/signature/send`, que es lo que dispara "Firmar KYC".
 * Los campos coinciden con `ISignature` para poder pasarla al SignatureComponent tal cual.
 */
export interface KycSignatureDto
{
    id: number;
    createdAt: string;
    expiresAt?: string | null;
    signAt?: string | null;
    /** Enum numérico de SignatureStatus (0 Pending, 1 Signed, 2 Sent, 3 Error). */
    status: number;
    phone?: string | null;
    countryPrefix?: string | null;
}

export interface UserIdentityBySavingPlanDto
{
    id: number;
    firstName: string;
    lastName: string;
    secondLastName: string;
    fullName: string;
    email: string;
    kycCompleted: boolean;
    role: string;
}

export interface SaveUserIdentityDraftResponseDto
{
    userIdentityId: number;
    /** True solo si al interviniente le falta su alta en Omnia/NextPortfolio: es lo que habilita encadenar `processUserIdentity`. */
    syncPending?: boolean;
}

export interface ProcessAndSyncUserIdentityResponseDto
{
    errors: string[];
}

export interface ChangedDocumentsDto
{
    dni: boolean;
    proofOfIncome: boolean;
    proofOfIncomeMinor: boolean;
}

export interface SaveUserIdentityAddressDto
{
    roadType?: string;
    streetName?: string;
    streetNumber?: string;
    streetFloor?: string;
    streetStair?: string;
    streetDoor?: string;
    streetOther?: string;
    zipCode?: string;
    city?: string;
    state?: string;
    country?: string;
}

export interface SaveUserIdentityDto
{
    id: number;
    firstName?: string;
    lastName?: string;
    secondLastName?: string;
    dni?: string;
    birthday?: string | null;
    idExpiration?: string | null;
    /** Validez permanente del documento. El backend traduce el flag a su fecha centinela. */
    hasPermanentIdExpiration?: boolean;
    birthdayCountry?: string;
    nationalCountry?: string;
    idType?: string;
    idFront?: string;
    idBack?: string;
    proofOfIncome?: string;
    proofOfMinorGuardianDescription?: string;
    gafiQuestion?: boolean;
    publicPosition?: boolean;
    email?: string;
    phone?: string;
    countryPrefix?: string;
    operationPhone?: string;
    operationCountryPrefix?: string;
    signPhone?: string;
    signCountryPrefix?: string;
    useSinglePhone?: boolean;
    fiscalAddress?: SaveUserIdentityAddressDto;
    postalAddress?: SaveUserIdentityAddressDto;
    /** true = la dirección fiscal coincide con la postal (el backend limpia la fiscal);
     *  false = son distintas (se envía fiscalAddress). Debe viajar siempre para no dejar
     *  el SameAddress persistido en NULL, que rompería la formalización. */
    sameAddress?: boolean;
    main?: boolean;
    userId?: number;
    maritalStatus?: number;
    residenceCountry?: string;
    fatcaStatus?: number;
    fatcaDocument?: string;
    hasFiscalNumberEEUU?: boolean;
    hasResidenceEEUU?: boolean;
    hasTaxesEEUU?: boolean;
    isUsCitizen?: boolean;
    residenceDocumentExpiration?: string;
    residenceOfficialDocument?: number;
    noResidenceDocument?: number;
    originCountryDocument?: string;
    profession?: string;
    economicActivity?: string;
    // Colecciones con semántica de REEMPLAZO: undefined = no tocar; lista (incluso vacía) = sustituir todas las filas.
    annualGrossIncome?: string;
    hasEmployedActivity?: boolean;
    hasSelfEmployedActivity?: boolean;
    employedActivities?: SaveKycEmployedActivityDto[];
    selfEmployedActivities?: SaveKycSelfEmployedActivityDto[];
    fundsOrigin?: string;
    fundsDocumentationProvided?: string;
    internationalCommercialActivity?: boolean;
    isPepRelative?: boolean;
    numberOfDependants?: number;
    otherNationalities?: boolean;
    otherNationality?: string;
    otherPersonalCircumstances?: string;
    riskCountries?: boolean;
    riskJurisdictionActivity?: boolean;
    maritalSituation?: string;
}

/** Fila de actividad por cuenta ajena del bloque KYC (mismo contrato en guardado y detalle). */
export interface SaveKycEmployedActivityDto
{
    activityStatus?: string | null;
    cnoCode?: string | null;
    years?: number | null;
    employedCurrentCompany?: string | null;
}

/** Fila de actividad por cuenta propia del bloque KYC (mismo contrato en guardado y detalle). */
export interface SaveKycSelfEmployedActivityDto
{
    activityStatus?: string | null;
    cnaeCode?: string | null;
    years?: number | null;
    relatedProfessionalActivity?: string | null;
}

export interface RequestUserIdentityAccessResultDto
{
    action: string;
}

export interface CatalogItemDto
{
    Value: number;
    Label: string;
}

/** Ítem de catálogo KYC: los enums KYC viajan como código string + literal. */
export interface KycCatalogItemDto
{
    code: string;
    label: string;
}

/** Igual que `UserIdentityCatalogs` en el endpoint `userIdentities/catalogs`. */
export interface UserIdentityCatalogs
{
    MaritalStatuses?: CatalogItemDto[] | null;
    FatcaStatuses?: CatalogItemDto[] | null;
    ResidenceOfficialDocuments?: CatalogItemDto[] | null;
    NoResidenceDocuments?: CatalogItemDto[] | null;
    /** Rellenado en cliente con `getJobs().data`, no suele venir de `GET .../catalogs`. */
    Professions?: UserIdentityProfessionCatalogItemDto[] | null;
    AnnualGrossIncomes?: KycCatalogItemDto[] | null;
    FundsOrigins?: KycCatalogItemDto[] | null;
    FundsDocumentationTypes?: KycCatalogItemDto[] | null;
    PersonalCircumstances?: KycCatalogItemDto[] | null;
    MaritalSituations?: KycCatalogItemDto[] | null;
    RelatedProfessionalActivities?: KycCatalogItemDto[] | null;
    EconomicActivityStatuses?: KycCatalogItemDto[] | null;
    /** Matriz origen de fondos → códigos de documentación acreditativa permitidos. */
    AllowedFundsDocumentationByOrigin?: Record<string, string[]> | null;
    /** Rellenados en cliente con `getProfessionCatalogs().data` (catálogos Cno/Cnae de Omnia). */
    Cnos?: ProfessionCatalogItemDto[] | null;
    Cnaes?: ProfessionCatalogItemDto[] | null;
}

export interface AccessRequestItemDto
{
    accessRequestId: number;
    userIdentityId: number;
    guestEmail: string;
    userIdentityFirstName: string;
    userIdentityLastName: string;
    status: string;
    createdDate?: string | null;
    approvalDate?: string | null;
    operation?: string | null;
    revocationDate?: string | null;
    revocationType?: string | null;
}

export interface AccessRequestsDto
{
    guestRequests: AccessRequestItemDto[];
    ownRequests: AccessRequestItemDto[];
}

export interface ManageAccessRequestIdentityPayload
{
    accessRequestId: number;
    userIdentityId: number;
    status: "Approved" | "Rejected";
    // Firma ya verificada: el backend exige un entero > 0. El cliente no manda null; si llega, es 400
    // ACCESS_REQUEST_SIGNATURE_REQUIRED (envelope), no 500.
    signatureId: number;
}

export interface RevokeUserIdentityAccessPayload
{
    userIdentityId: number;
    guestUserId: number;
}

export interface RenounceUserIdentityAccessPayload
{
    userIdentityId: number;
}

/** Cuerpo de POST userIdentities/AcceptTransferOwnership/{userIdentityId}. */
export interface AcceptTransferOwnershipPayload
{
    /** Además de aceptar, lo convierte en el principal de la cuenta (borra el principal vacío). Irreversible. */
    promoteToMain: boolean;
}

interface HttpErrorMessages
{
    unauthorized?: string;
    notFound?: string;
    forbidden?: string;
    unprocessable?: string;
    internalServer?: string;
    fallback: string;
}

const defaultHttpErrorMessages: Omit<HttpErrorMessages, "fallback"> =
{
    unauthorized: "Tu sesión ha caducado o no tienes autorización. Vuelve a iniciar sesión.",
    notFound: "No se encontró el recurso solicitado.",
    forbidden: "No tienes permiso para realizar esta acción.",
    unprocessable: "No se pudo procesar la operación en el estado actual del interviniente. Revísalo e inténtalo de nuevo.",
    internalServer: "Error interno del servidor. Inténtalo de nuevo más tarde.",
};

function resolveHttpStatusMessage(status: number | undefined, messages: HttpErrorMessages): string | null
{
    if(status === 401 && messages.unauthorized)
    {
        return messages.unauthorized;
    }
    if(status === 403 && messages.forbidden)
    {
        return messages.forbidden;
    }
    if(status === 404 && messages.notFound)
    {
        return messages.notFound;
    }
    if(status === 422 && messages.unprocessable)
    {
        return messages.unprocessable;
    }
    if(status === 500 && messages.internalServer)
    {
        return messages.internalServer;
    }
    return null;
}

function mapHttpErrorToMessage(err: unknown, messages: HttpErrorMessages): string
{
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    return resolveHttpStatusMessage(status, messages) ?? messages.fallback;
}

function postWithErrorHandling(url: string, payload: unknown, fallbackCode: string, messages: HttpErrorMessages): Promise<EmptyIdentityOrchestratorEnvelope>
{
    return axios.post<IdentityOrchestratorEnvelope<Record<string, never>>>(url, payload, {validateStatus: orchestratorValidateStatus}).then((req) =>
    {
        if(redirectToLoginIfUnauthorized(req.status))
        {
            return{
                success: false,
                data: null,
                flow: null,
                errors: [{code: "401", message: "Sesión no válida. Redirigiendo al inicio de sesión."}],
            };
        }
        const reqData = req.data;
        if(reqData && typeof reqData === "object" && "success" in reqData)
        {
            const envelope = reqData as EmptyIdentityOrchestratorEnvelope;
            // Si el servidor devuelve success:false sin errors, añadir mensaje de fallback
            if(!envelope.success && (!envelope.errors || envelope.errors.length === 0))
            {
                return{...envelope, errors: [{code: fallbackCode, message: messages.fallback}]};
            }
            return envelope;
        }
        return{
            success: false,
            data: null,
            flow: null,
            errors: [{code: req.status ? String(req.status) : "INVALID_RESPONSE", message: resolveHttpStatusMessage(req.status, messages) ?? messages.fallback}],
        };
    })
    .catch((err) =>
    {
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        const code = status !== undefined ? String(status) : fallbackCode;
        return{
            success: false,
            data: null,
            flow: null,
            errors:
            [
                {
                    code,
                    message: mapHttpErrorToMessage(err, messages),
                }
            ]
        };
    });
}

const revokeAccessErrorMessages: HttpErrorMessages =
{
    ...defaultHttpErrorMessages,
    notFound: "No se encontró el interviniente o el usuario asociado indicado.",
    forbidden: "No tienes permiso para revocar el acceso a este usuario.",
    fallback: "No se pudo revocar el acceso.",
};

const renounceAccessErrorMessages: HttpErrorMessages =
{
    ...defaultHttpErrorMessages,
    notFound: "No se encontró el interviniente indicado.",
    forbidden: "No tienes permiso para renunciar al acceso a este interviniente.",
    fallback: "No se pudo renunciar al acceso.",
};

const deleteIdentityErrorMessages: HttpErrorMessages =
{
    ...defaultHttpErrorMessages,
    notFound: "No se encontró el interviniente a eliminar.",
    forbidden: "No tienes permiso para eliminar este interviniente.",
    fallback: "No se pudo eliminar el interviniente.",
};

export interface AddressDto
{
    id?: number | null;
    streetFloor?: string | null;
    streetStair?: string | null;
    streetDoor?: string | null;
    streetOther?: string | null;
    updatedAt?: string | null;
    roadType: string;
    streetName: string;
    streetNumber: string;
    city: string;
    zipCode: string;
    state: string;
    country: string;
    createdAt: string;
}

export interface IdentityPermissionsDto
{
    canEdit: boolean;
    canView: boolean;
    accessMode: string;
}

export interface UserIdentityDetailDto
{
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    secondLastName?: string | null;
    dni?: string | null;
    birthday?: string | null;
    birthdayCountry?: string | null;
    nationalCountry?: string | null;
    status?: UserIdentityStatusEnum | null;
    idType?: string | null;
    idExpiration?: string | null;
    hasPermanentIdExpiration?: boolean | null;
    email?: string | null;
    phone?: string | null;
    countryPrefix?: string | null;
    operationPhone?: string | null;
    operationCountryPrefix?: string | null;
    signPhone?: string | null;
    signCountryPrefix?: string | null;
    fiscalAddress?: AddressDto | null;
    postalAddress?: AddressDto | null;
    hasDniFront: boolean;
    hasDniBack: boolean;
    hasProofOfIncome: boolean;
    hasProofOfMinorGuardian: boolean;
    idFront?: string | null;
    idBack?: string | null;
    proofOfIncome?: string | null;
    proofOfMinorGuardianDescription?: string | null;
    maritalStatus?: number | null;
    residenceCountry?: string | null;
    fatcaStatus?: number | null;
    fatcaDocument?: string | null;
    hasFiscalNumberEEUU?: boolean | null;
    hasResidenceEEUU?: boolean | null;
    hasTaxesEEUU?: boolean | null;
    isUsCitizen?: boolean | null;
    residenceDocumentExpiration?: string | null;
    residenceOfficialDocument?: number | null;
    noResidenceDocument?: number | null;
    originCountryDocument?: string | null;
    profession?: string | number | null;
    publicPosition?: string | null;
    gafiQuestion?: boolean | null;
    economicActivity?: string | number | null;
    isMinor?: boolean | null;
    main: boolean;
    permissions: IdentityPermissionsDto;
    annualGrossIncome?: string | null;
    hasEmployedActivity?: boolean | null;
    hasSelfEmployedActivity?: boolean | null;
    employedActivities?: SaveKycEmployedActivityDto[] | null;
    selfEmployedActivities?: SaveKycSelfEmployedActivityDto[] | null;
    fundsOrigin?: string | null;
    fundsDocumentationProvided?: string | null;
    internationalCommercialActivity?: boolean | null;
    isPepRelative?: boolean | null;
    numberOfDependants?: number | null;
    otherNationalities?: boolean | null;
    otherNationality?: string | null;
    otherPersonalCircumstances?: string | null;
    riskCountries?: boolean | null;
    riskJurisdictionActivity?: boolean | null;
    maritalSituation?: string | null;
}

export const AccountTypes =
[
    {
        key: "0",
        name: "Titular principal",
        value: "owner",
    },
    {
        key: "1",
        name: "Titular",
        value: "authorized",
    },
    {
        key: "2",
        name: "Representante legal",
        value: "representative",
    },
    {
        key: "3",
        name: "Titular menor de edad",
        value: "minor",
    },
];

class UserIdentitiesService
{
    ///Nueva zona de Onboarding
    public renounceAccessToUserIdentity = async (userIdentityId: number): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${userIdentityId}/renounce-access`;
        const req = await axios.delete(url);
        return req.data;
    }
    public getUserIdentitiesByUserId = async (userId: number): Promise<IdentityOrchestratorEnvelope<Identity[]>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/byUser/${userId}`;
        return requestIdentityOrchestrator<Identity[]>({method: "GET", url});
    };

    public getUserIdentitiesBySavingPlan = async (savingsPlanId: number): Promise<IdentityOrchestratorEnvelope<UserIdentityBySavingPlanDto[]>> =>
    {
        const url: string = `${proxyUrl}/UserIdentitiesBySavingPlan/${savingsPlanId}`;
        return requestIdentityOrchestrator<UserIdentityBySavingPlanDto[]>({method: "GET", url});
    };

    public getAllAccessRequests = async (): Promise<IdentityOrchestratorEnvelope<AccessRequestsDto>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/AllAccessRequests`;
        return requestIdentityOrchestrator<AccessRequestsDto>({method: "GET", url});
    };

    public saveUserIdentityDraft = async (userIdentityId: number, multipartBody: FormData): Promise<IdentityOrchestratorEnvelope<SaveUserIdentityDraftResponseDto>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/saveUserIdentity/${userIdentityId}`;
        return requestIdentityOrchestrator<SaveUserIdentityDraftResponseDto>(
        {
            method: "PATCH",
            url,
            data: multipartBody,
            headers: { "content-type": "multipart/form-data" },
        });
    };

    public deleteIdentity = async (userIdentityId: number): Promise<EmptyIdentityOrchestratorEnvelope> =>
    {
        const url: string = `${proxyUrl}/userIdentities/Delete/${userIdentityId}`;
        return postWithErrorHandling(url, {}, "DeleteIdentityRequestFailed", deleteIdentityErrorMessages);
    };

    public analyzeDocument = async (formData: FormData): Promise<IdentityOrchestratorEnvelope<IdentityDocumentAnalysisResponse>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/analyzeDocument`;
        return requestIdentityOrchestrator<IdentityDocumentAnalysisResponse>(
        {
            method: "POST",
            url,
            data: formData,
            headers: { "content-type": "multipart/form-data" },
        });
    };

    public analyzeIdentityStatus = async (dni: string): Promise<IdentityOrchestratorEnvelope<UserIdentityStatusDto>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/analyzeStatus/${encodeURIComponent(dni)}`;
        return requestIdentityOrchestrator<UserIdentityStatusDto>({method: "GET", url});
    };

    public manageAccessRequestIdentity = async (payload: ManageAccessRequestIdentityPayload): Promise<EmptyIdentityOrchestratorEnvelope> =>
    {
        const url: string = `${proxyUrl}/userIdentities/manageAccessRequest`;
        return requestIdentityOrchestrator<Record<string, never>>({method: "POST", url, data: payload});
    };

    public requestUserIdentityAccess = async (userIdentityId: number, payload?: unknown): Promise<IdentityOrchestratorEnvelope<RequestUserIdentityAccessResultDto>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/RequestAccess/${userIdentityId}`;
        return requestIdentityOrchestrator<RequestUserIdentityAccessResultDto>({method: "POST", url, data: payload ?? {}});
    };

    /**
     * Acepta la cesión de un interviniente recibido por cesión aprobada (estado TRANSFER): pasa a PENDING (u
     * OUTDATED). Con `promoteToMain` además pasa a ser el principal de la cuenta y el principal vacío de
     * nacimiento se borra por completo, de forma irreversible. Éxito: sobre vacío. Errores: 409
     * MAIN_PROMOTION_NOT_ALLOWED (rechazo genérico, sea cual sea el motivo) o MAIN_PROMOTION_BLOCKED (soporte).
     */
    public acceptTransferOwnership = async (userIdentityId: number, promoteToMain: boolean): Promise<EmptyIdentityOrchestratorEnvelope> =>
    {
        const url: string = `${proxyUrl}/userIdentities/AcceptTransferOwnership/${userIdentityId}`;
        const payload: AcceptTransferOwnershipPayload = {promoteToMain: promoteToMain === true};
        return requestIdentityOrchestrator<Record<string, never>>({method: "POST", url, data: payload});
    };

    public renounceUserIdentityAccess = async (payload: RenounceUserIdentityAccessPayload): Promise<EmptyIdentityOrchestratorEnvelope> =>
    {
        const url: string = `${proxyUrl}/userIdentities/RenounceAccess`;
        return postWithErrorHandling(url, payload, "RenounceAccessRequestFailed", renounceAccessErrorMessages);
    };

    public revokeUserIdentityAccess = async (payload: RevokeUserIdentityAccessPayload): Promise<EmptyIdentityOrchestratorEnvelope> =>
    {
        const url: string = `${proxyUrl}/userIdentities/RevokeAccess`;
        return postWithErrorHandling(url, payload, "RevokeAccessRequestFailed", revokeAccessErrorMessages);
    };

    public getUserIdentityCatalogs = async (): Promise<IdentityOrchestratorEnvelope<UserIdentityCatalogs>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/catalogs`;
        return requestIdentityOrchestrator<UserIdentityCatalogs>({method: "GET", url});
    };

    public getUserIdentityDetails = async (userIdentityId: number): Promise<IdentityOrchestratorEnvelope<UserIdentityDetailDto>> =>
    {
        const url: string = `${proxyUrl}/userIdentityDetails/${userIdentityId}`;
        return requestIdentityOrchestrator<UserIdentityDetailDto>({method: "GET", url});
    };

    public getMainIdentityByUserId = async (userId: number): Promise<IdentityOrchestratorEnvelope<MainUserIdentityDto>> =>
    {
        const url: string = `${proxyUrl}/UserIdentities/GetMainIdentityByUserId/${userId}`;
        return requestIdentityOrchestrator<MainUserIdentityDto>({method: "GET", url});
    };

    public processUserIdentity = async (userIdentityId: number): Promise<IdentityOrchestratorEnvelope<ProcessAndSyncUserIdentityResponseDto>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/process/${userIdentityId}`;
        return requestIdentityOrchestrator<ProcessAndSyncUserIdentityResponseDto>(
        {
            method: "POST",
            url,
        });
    };

    public getJobs = async (): Promise<IdentityOrchestratorEnvelope<UserIdentityJobOrchestratorItemDto[]>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/jobs`;
        return requestIdentityOrchestrator<UserIdentityJobOrchestratorItemDto[]>({method: "GET", url});
    };

    /**
     * Catálogos de profesión de Omnia: CNO (cuenta ajena) y CNAE (cuenta propia).
     * Segmento de ruta opcional para restringir: "cno" | "cnae"; sin segmento devuelve ambos.
     */
    public getProfessionCatalogs = async (catalog?: "cno" | "cnae"): Promise<IdentityOrchestratorEnvelope<ProfessionCatalogsDto>> =>
    {
        const segment = catalog ? `/${catalog}` : "";
        const url: string = `${proxyUrl}/userIdentities/professionCatalogs${segment}`;
        return requestIdentityOrchestrator<ProfessionCatalogsDto>({method: "GET", url});
    };

    /**
     * Matriz de estado KYC de los intervinientes relacionados del usuario, tomando uno como
     * referencia. Una fila por interviniente; el estado lo decide el backend.
     */
    public getUserIdentityKycStatusMatrix = async (userIdentityId: number): Promise<IdentityOrchestratorEnvelope<KycStatusRowDto[]>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/${userIdentityId}/kyc/status`;
        return requestIdentityOrchestrator<KycStatusRowDto[]>({method: "GET", url});
    };

    /**
     * Comprobación de caducidad del KYC de los intervinientes del usuario. Se dispara UNA SOLA VEZ por login
     * desde `checkUser()`: el login no ocurre en la API .NET (vive en el servidor de autenticación), así que
     * el backend no puede enterarse por sí mismo. El backend pasa a OUTDATED a quien deba re-firmar y deja el
     * aviso en la barra de estado. Nunca falla hacia el cliente: ante cualquier problema devuelve un resumen
     * vacío. No se debe esperar su resultado para pintar la aplicación.
     */
    public checkKycExpiration = async (userId: number): Promise<IdentityOrchestratorEnvelope<KycExpirationSummaryDto>> =>
    {
        const url: string = `${proxyUrl}/users/${userId}/kyc/expirationCheck`;
        return requestIdentityOrchestrator<KycExpirationSummaryDto>({method: "POST", url});
    };

    /**
     * Solicita al propietario de un interviniente PRESTADO que actualice y re-firme su KYC caducado, para poder
     * incluirlo en un contrato. Deja un aviso en su barra de estado; no envía correo. Idempotente por
     * propietario: si ya tiene una solicitud viva, responde `alreadyNotified` y no crea otra.
     */
    public notifyKycOwner = async (userIdentityId: number): Promise<IdentityOrchestratorEnvelope<KycOwnerNotificationDto>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/${userIdentityId}/kyc/notifyOwner`;
        return requestIdentityOrchestrator<KycOwnerNotificationDto>({method: "POST", url});
    };

    /**
     * Genera (o devuelve, si ya existe pendiente) la versión firmable del KYC del interviniente
     * (`POST userIdentities/{id}/kyc`). Idempotente: reintentos devuelven el mismo kycId. Debe llamarse
     * antes de pedir el documento pendiente, porque el visor solo existe una vez generada la versión.
     */
    public generateUserIdentityKyc = async (userIdentityId: number): Promise<IdentityOrchestratorEnvelope<KycDocumentDto>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/${userIdentityId}/kyc`;
        return requestIdentityOrchestrator<KycDocumentDto>({method: "POST", url});
    };

    /**
     * Documento KYC pendiente de firma del interviniente: PDF en base64 dentro del envelope.
     * Si Omnia no dispone de él lo indica ella (KYC_DOCUMENT_NOT_AVAILABLE → 404).
     */
    public getUserIdentityKycDocument = async (userIdentityId: number): Promise<IdentityOrchestratorEnvelope<KycDocumentDownloadDto>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/${userIdentityId}/kyc/document`;
        return requestIdentityOrchestrator<KycDocumentDownloadDto>({method: "GET", url});
    };

    /**
     * Crea la firma OTP del KYC del interviniente, DESARMADA: existe el registro pero sin ventana de
     * expiración y sin SMS enviado. Cazada por id, no por DNI: el frontend nunca maneja el documento de
     * identidad del interviniente y el backend comprueba la propiedad.
     */
    public requestUserIdentityKycSignature = async (userIdentityId: number): Promise<IdentityOrchestratorEnvelope<KycSignatureDto>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/${userIdentityId}/kyc/signature`;
        return requestIdentityOrchestrator<KycSignatureDto>({method: "POST", url});
    };

    /**
     * Arma la firma del KYC y envía el SMS: genera el código y abre la ventana de 5 minutos. Se llama al
     * pulsar "Firmar KYC", ya leído el documento, para que quien solo lo consulta no reciba ningún SMS y
     * para que los 5 minutos empiecen a contar cuando el interviniente pide firmar de verdad.
     */
    public sendUserIdentityKycSignature = async (userIdentityId: number, signatureId: number): Promise<IdentityOrchestratorEnvelope<KycSignatureDto>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/${userIdentityId}/kyc/signature/send`;
        return requestIdentityOrchestrator<KycSignatureDto>({method: "POST", url, data: {signatureId}});
    };

    /**
     * Re-firma standalone del KYC de un interviniente OUTDATED: dada la firma OTP ya verificada
     * (`PUT signatures`), el backend genera (idempotente), firma en Omnia y activa (OUTDATED→ACTIVE).
     */
    public signUserIdentityKyc = async (userIdentityId: number, signatureId: number): Promise<IdentityOrchestratorEnvelope<unknown>> =>
    {
        const url: string = `${proxyUrl}/userIdentities/${userIdentityId}/kyc/sign`;
        return requestIdentityOrchestrator<unknown>({method: "POST", url, data: {signatureId}});
    };

    //legacy de UserIdentities

    public getUserIdentity = async (userIdentityId: number): Promise<UserIdentityType> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${userIdentityId}`;
        const req = await axios.get<UserIdentityType>(url);
        return req.data;
    };

    public getUserIdentityWithSuitabilityTests = async (userIdentityId: number): Promise<UserIdentityType> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${userIdentityId}?includes=SuitabilityTests`;
        const req = await axios.get<UserIdentityType>(url);
        return req.data;
    };
    public createUserIdentity = async (userIdentity: UserIdentityType): Promise<UserIdentityType> =>
    {
        if(userIdentity.sameAddress === undefined || userIdentity.sameAddress)
        {
            delete userIdentity.fiscalAddress;
            delete userIdentity.fiscalAddressId;
        }
        let url: string = `${proxyUrl}/userIdentities`;
        let formData = new FormData();
        let data: any = {};
        Object.keys(userIdentity).forEach((field) =>
        {
            // @ts-ignore
            if(userIdentity[field]?.size !== undefined)
            {
                // @ts-ignore
                formData.append(field, userIdentity[field]);
            }
            else
            {
                // @ts-ignore
                data[field] = userIdentity[field];
            }
        });

        formData.append("data", JSON.stringify(data));
        const req = await axios.post(url, formData,
        {
            headers: {"content-type": "multipart/form-data"},
        });
        return req.data;
    };
    public patch = async (userIdentityId: string, data: any): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${userIdentityId}`;
        const req = await axios.patch(url, data);
        return req.data;
    };
    public updateUserIdentity = async (userIdentity: UserIdentityType): Promise<UserIdentityType> =>
    {
        if(userIdentity.sameAddress === null || userIdentity.sameAddress)
        {
            delete userIdentity.fiscalAddress;
            delete userIdentity.fiscalAddressId;
        }
        let url: string = `${proxyUrl}/userIdentities/${userIdentity.id}`;
        let formData = new FormData();
        let data: any = {};
        Object.keys(userIdentity).forEach((field) =>
        {
            // @ts-ignore
            if(userIdentity[field]?.size !== undefined)
            {
                // @ts-ignore
                formData.append(field, userIdentity[field]);
            }
            else
            {
                // @ts-ignore
                data[field] = userIdentity[field];
            }
        });

        formData.append("data", JSON.stringify(data));
        const req = await axios.put(url, formData,
        {
            headers: { "content-type": "multipart/form-data" },
        });
        return req.data;
    };
    public getIdentitiesBySavingsPlanId = async (savingsPlanId: number): Promise<UserIdentityType[]> =>
    {
        let url: string = `${proxyUrl}/userIdentities/getBySavingsPlanId/${savingsPlanId}`;
        const req = await axios.get(url);
        return req.data;
    };
    public getIdentitiesByUserId = async (userId: number): Promise<UserIdentityType[]> =>
    {
        let url: string = `${proxyUrl}/userIdentities/getByUserId/${userId}`;
        const req = await axios.get(url);
        return req.data;
    };
    public getLastIdentityByUserId = async (userId: number): Promise<UserIdentityType> =>
    {
        let url: string = `${proxyUrl}/userIdentities/getLastByUserId/${userId}`;
        const req = await axios.get(url);
        return req.data;
    };
    public getRefundCode = async (userId: number): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${userId}/getRefundCode`;
        const req = await axios.get(url);
        return req.data;
    };
    public isRefundCodeDefined = async (userIdentityId: number): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${userIdentityId}/isRefundCodeDefined`;
        const req = await axios.get(url);
        return req.data;
    };
    public refundKeyRequest = async (signatureId: number, userIdentityId: number): Promise<void> =>
    {
        const url = `${proxyUrl}/userIdentities/RefundKeyRequest`;
        await axios.post(url, {signatureId, userIdentityId});
    };
    public checkByIdDocumentAndBirthday = async (userIdentityId: number, toSend: any): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${userIdentityId}/checkByIdDocumentAndBirthday`;
        const req = await axios.post<any>(url, toSend);
        return req.data;
    };
    public addSuitabilityTestSignatureToUserIdentity = async (userIdentityId: number): Promise<UserIdentityType> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${userIdentityId}/suitabilityTestSignatures`;
        const req = await axios.post<any>(url, {});
        return req.data;
    };
    public validateUniqueDocument = async (finametrixId: string, idDocument: string, id: number): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/userIdentities/validateUniqueDocument/${finametrixId}/${idDocument}/${id}`;
        const req = await axios.get(url);
        return req.data;
    };
    public getInstrumentsIds = async (userIdentityId: number): Promise<string[]> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${userIdentityId}/instrumentsIds`;
        const req = await axios.get(url);
        return req.data;
    };
    public userIdentityExists = async (idDocument: string): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${idDocument}/verify`;
        const req = await axios.get(url);
        return req.data;
    };
    public AMLVerification = async (userIdentityId: number, idDocument: string): Promise<AmlVerificationResponse> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${userIdentityId}/aml-verification/${idDocument}`;
        const req = await axios.get(url);
        return req.data;
    };
    public deleteUserIdentity = async (userIdentityId: number): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${userIdentityId}`;
        const req = await axios.delete(url);
        return req.data;
    };
    public getUSerIdentityByIdDocument = async (idDocument: string): Promise<UserIdentityType> =>
    {
        let url: string = `${proxyUrl}/userIdentities/getByIdocument/${idDocument}`;
        const req = await axios.get(url);
        return req.data;
    };
    public postUserIdentityAccessRequest = async (requestingUserId: Number, targetUserIdentity: Number, requestedUserId: Number, savingPlanId: Number): Promise<UserIdentityAccessRequest> =>
    {
        let url: string = `${proxyUrl}/userIdentities/access-request`;

        let formData = new FormData();
        formData.append("savingPlanId", savingPlanId.toString());
        formData.append("requestingUserId", requestingUserId.toString());
        formData.append("targetUserIdentity", targetUserIdentity.toString());
        formData.append("requestedUserId", requestedUserId.toString());

        const req = await axios.post(url, formData,
        {
            headers: { "content-type": "multipart/form-data" },
        });
        return req.data;
    };
    public getAllAccessRequestsByUserId = async (requestedUserId: Number): Promise<AccessRequestResponse> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${requestedUserId}/get-access-requests`;
        const req = await axios.get(url);
        return req.data;
    };

    public getAccessRequestMode = async (userId: number, targetUserIdentityId: number): Promise<AccessRequestMode> =>
    {
        let url: string = `${proxyUrl}/userIdentities/${userId}/access-request-mode/${targetUserIdentityId}`;
        const req = await axios.get(url);
        return req.data;
    };

    public patchAccessRequestStatus = async (accessRequest: AccessRequestDto): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/userIdentities/access-request`;
        const req = await axios.patch(url, accessRequest);
        return req.data;
    };

    public deleteAccessRequestById = async (accessRequestId: number): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/userIdentities/remove-access-request/${accessRequestId}`;
        const req = await axios.delete(url);
        return req.data;
    };
}

const userIdentities = new UserIdentitiesService();
export default userIdentities;